import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import {
  EmptyStateIcon,
  usePolling,
  AlertService,
  OverlayService,
  Flyout,
  Theme,
  Button,
  MetricCard,
  SparkleGradientIcon,
} from '@spoiler-alert/ui-library';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { TitleService } from '../../services';
import { Breadcrumbs, Store } from '../../store';
import BuyersFlyup from '../offer-comparison/buyers-flyup';
import { trucklaneInitializePollingQuery, trucklanePollingQuery, trucklaneViewQuery, trucklaneExceptionsQuery } from '../../graphql/queries';
import { initialMetrics, generateMetrics, TrucklaneActionInProgressContext } from './trucklane-utility';
import RecalculationBanner from './recalculation-banner';
import StaleTabBanner from './stale-tab-banner';
import StaticLoading from '../static-loading';
import { featureFlags, offerlistingStatuses, subscriptionTier } from '../../enums';
import routePaths from '../../route-paths';
import TrucklaneDrilldown from './trucklane-drilldown';
import TrucklaneFilter from './trucklane-filter';
import client from '../../apollo/client';
import { ForceRecalculation } from '../../graphql/mutations';
import { Route } from 'react-router-dom';
import SuggestedNegotiationsVisibilityBanner from '../../components/banners/suggested-negotiations-visibility';
import { checkFeature } from '../../helpers';
import OfferShiftContext from '../offer-comparison/offer-shift-context';
import { ACTIVE_STEP } from '../offer-comparison/active-step-enums';

// How long after a user action, or after a Recalculate refresh, we keep absorbing the offer-shift
// reallocation it triggers. The backend reallocation completes within a few seconds; the bound keeps a
// later, genuinely external reallocation from being swallowed.
const BID_REALLOCATION_ABSORB_MS = 60_000;

// The out-of-sync refresh found nothing to fetch, so the page reloads every lane instead.
const NO_OUT_OF_SYNC_CHANNELS_FOUND = 'NO_OUT_OF_SYNC_CHANNELS_FOUND';
const TRUCKLANES_RELOAD_FAILED = 'Truck lanes could not be refreshed. Please reload the page.';

// True when a poll shows the backend finished recalculating: at least one lane, every lane Valid and
// no bid reallocation running. An error or stale poll returns no lanes, so it never counts.
const recalcFinished = (pollResponse) =>
  !!pollResponse?.trucklaneChannels?.length &&
  pollResponse.trucklaneChannels.every((channel) => channel.status === 'Valid') &&
  !pollResponse.bidReallocation?.processing;

const styles = {
  container: {
    minHeight: 'calc(100vh - 114px)',
  },
  spacing: {
    paddingBottom: 36,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  marginRow: {
    extend: 'row',
    marginBottom: 24,
  },
  stickyRow: {
    extend: 'row',
    position: 'sticky',
    zIndex: 9,
    top: '45px',
    padding: '24px 0px',
    backgroundColor: Theme.white,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontSize: '24px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '500',
  },
  settingsWrapper: {
    width: '100%',
  },
  footer: {
    top: '1vh',
  },
  filterFlyout: {
    extend: 'marginRow',
    '& h4': {
      margin: 0,
      marginRight: 'auto',
      fontSize: '1rem',
      fontWeight: 500,
    },
    '& a': {
      color: Theme.linkTextColor,
      cursor: 'pointer',
      fontSize: '0.875rem',
    },
  },
  filterHeader: {
    marginBottom: 24,
    '& h2': {
      fontSize: '14px',
      fontWeight: 500,
      marginBottom: 0,
    },
  },
  loader: {
    display: 'flex',
    height: '100vh',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  drilldownContainer: {
    height: '100%',
  },
  drilldown: {
    width: '90%',
  },
  drilldownContents: {
    padding: '0px',
  },
};
const useStyles = createUseStyles(styles);

const Trucklane = ({ user, history }) => {
  const classes = useStyles();
  const [recalculation, setRecalculation] = useState(false);
  const [smartAwardSettingUpdate, setSmartAwardSettingUpdate] = useState(false);
  // Counts polls that finish while waiting on a Recalculate click, including ones that return the
  // same data as the last (see the wait effect below the poll effect).
  const [recalcPollCount, setRecalcPollCount] = useState(0);
  const [staleTab, setStaleTab] = useState(false);
  const [trucklaneId, setTrucklaneId] = useState(() => {
    const trucklanePath = location.pathname.split('/');
    return trucklanePath[trucklanePath.length - 1] !== 'trucklane' ? trucklanePath[trucklanePath.length - 1] : null;
  });
  const [trucklaneGroups, setTrucklaneGroups] = useState([]);
  const [channelError, setChannelError] = useState(false);
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [pollingData, setPollingData] = useState({ poll: false, interval: 0, initialLoad: true });
  const [activeStep, setActiveStepState] = useState(() => {
    const stepParam = new URLSearchParams(history.location.search).get('step');
    return Object.values(ACTIVE_STEP).includes(stepParam) ? stepParam : ACTIVE_STEP.SUGGESTIONS;
  });
  const setActiveStep = (step) => {
    setActiveStepState(step);
    const params = new URLSearchParams(history.location.search);
    if (step === ACTIVE_STEP.SUGGESTIONS) params.delete('step');
    else params.set('step', step);
    const search = params.toString();
    history.replace({ pathname: history.location.pathname, search: search ? `?${search}` : '' });
  };
  const [forceRecalculation, { loading: forcedRecalcLoading }] = useMutation(ForceRecalculation);
  const [
    getReservePriceExceptionSuggestions,
    { data: rpeData, previousData: previousRpeData, error: reservePriceExceptionError, loading: reservePriceExceptionLoading },
  ] = useLazyQuery(trucklaneExceptionsQuery, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const suggestedTrucklanesWithExceptions = rpeData ?? previousRpeData;
  const backgroundRpeRefreshRef = useRef(false);
  const rpeFetchedOnceRef = useRef(false);
  const rpeLoading = reservePriceExceptionLoading && !backgroundRpeRefreshRef.current;
  const { data: initPollingData, loading: initPollingLoad } = useQuery(trucklaneInitializePollingQuery);
  const { actionInProgress } = useContext(TrucklaneActionInProgressContext);
  const { offerShiftsMappedToTrucklanes = new Map() } = useContext(OfferShiftContext) || {};
  const pollingRef = useRef();
  // A user-initiated recalc triggers its own bid reallocation on the backend, which can complete
  // shortly after `refreshTrucklaneData` and bump `bidReallocation.updatedAt`. That bump is the
  // result of the recalc we just performed — not a new external change — so we absorb the first
  // bidReallocation change after a refresh instead of re-invalidating and re-showing the banner. Only
  // within BID_REALLOCATION_ABSORB_MS: the reallocation usually finishes before the refresh, and an
  // open-ended absorb then swallowed the next reallocation someone else caused, however much later.
  const skipNextBidReallocationUntilRef = useRef(0);
  // An ignore/restore/award action on this page can also kick off an offer-shift reallocation on the
  // backend (offerShiftRecalc.processing → true, then → false with a bumped updatedAt on completion).
  // That self-inflicted completion would otherwise read as a fresh external change and re-raise the
  // recalc banner a few seconds after the action. `skipBidReallocationUntilRef` holds a bounded
  // timestamp window, opened when an action starts, during which we absorb one such bump. The bound
  // ensures a genuinely external reallocation arriving after the window still surfaces the banner.
  const skipBidReallocationUntilRef = useRef(0);
  const {
    data: trucklaneData,
    loading: trucklaneDataLoad,
    fetchMore: getTrucklaneData,
  } = useQuery(trucklaneViewQuery, {
    skip: pollingData.initialLoad,
    notifyOnNetworkStatusChange: false,
    variables: {
      getOutOfSyncChannels: false,
    },
  });
  // Derive the view model straight from the live cache: the offer fragment already carries inventory
  // inline, so there's no second query or deep-copy merge. We only recompute `hasSuggestions` (a lane
  // has no actionable suggestions once none of its ACTIVE/IGNORED offers are still suggested), and we
  // do it immutably so Apollo's normalized cache stays the single source of truth.
  const originalTrucklanes = useMemo(() => {
    const trucklanes = trucklaneData?.trucklaneViewQuery?.trucklanes;
    if (!trucklanes?.length) return [];
    return trucklanes.map((trucklane) => {
      const hasActiveSuggestion = trucklane.offers.some(
        (offer) => [offerlistingStatuses.ACTIVE, offerlistingStatuses.IGNORED].includes(offer.status) && offer.suggestions?.award?.suggested,
      );
      return trucklane.hasSuggestions && !hasActiveSuggestion ? { ...trucklane, hasSuggestions: false } : trucklane;
    });
  }, [trucklaneData]);
  const suggestedNegotiationsEnabled = checkFeature(featureFlags.suggestedNegotiations);
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);
  const saasLiteCustomer = user.site.subscriptionTier === subscriptionTier.saasLite;
  const pollResponse = usePolling({
    idleInterval: pollingData.interval,
    poll: pollingData.poll,
    query: trucklanePollingQuery,
    responseKey: 'trucklanePollingQuery',
    parameters: { pollingDetails: trucklaneGroups },
    skip: !pollingData.poll,
    fetchPolicy: 'network-only',
    // Stop hammering the backend once the tab is no longer visible. The initial load and the
    // post-refresh load poll at 500ms to drive the page out of its loading state, so those are never
    // paused. The 5s steady-state poll and the wait after a Recalculate click are, and resume with an
    // immediate refetch on return.
    pauseWhenHidden: !pollingData.initialLoad,
    onPollComplete: () => {
      if (smartAwardSettingUpdate) setRecalcPollCount((count) => count + 1);
    },
  });
  const loading = initPollingLoad || trucklaneDataLoad || pollingData.initialLoad || smartAwardSettingUpdate || forcedRecalcLoading;
  // The full reload after a refresh that found nothing out of sync: whether one is in flight (it must
  // only start once), and whether it failed.
  const fullReloadInFlightRef = useRef(false);
  const [fullReloadFailed, setFullReloadFailed] = useState(false);

  const clearCache = () => {
    // Evict the polling query roots so polling restarts from a clean slate. Deliberately no
    // cache.gc(): the offer fragment now carries `inventory` inline as a normalized reference, and
    // garbage collection would reap those Inventory entities (no longer anchored by the removed
    // listedAndAcceptedInventoryQuery), leaving offer.inventory as a dangling null reference.
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'trucklanePollingQuery' });
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'trucklaneInitializePollingQuery' });
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'activeOfferShiftsQuery' });
    Store.clearValidTrucklaneChannels();
  };

  const refreshTrucklaneData = () => {
    // The recalc that just completed may bump bidReallocation.updatedAt once more when its own
    // reallocation finishes; ignore that bump so it doesn't re-invalidate freshly recalculated data.
    skipNextBidReallocationUntilRef.current = Date.now() + BID_REALLOCATION_ABSORB_MS;
    setFullReloadFailed(false);
    clearCache();
    setRecalculation(false);
    setPollingData({ poll: true, interval: 500, initialLoad: true });
    Store.clearTrucklaneChannels();
    getTrucklaneData({ variables: { getOutOfSyncChannels: true } });
  };

  // Open the absorb window as soon as a trucklane action begins, so the offer-shift reallocation it
  // triggers on the backend gets swallowed when it completes instead of re-raising the recalc banner.
  useEffect(() => {
    if (actionInProgress) skipBidReallocationUntilRef.current = Date.now() + BID_REALLOCATION_ABSORB_MS;
  }, [actionInProgress]);

  const enableRecalculation = (channels) => {
    let recalcFlag = false;
    channels.forEach((channel) => {
      if (channel.status !== 'Valid') {
        Store.invalidateTrucklaneChannel(channel);
        recalcFlag = true;
      }
    });
    if (recalcFlag) {
      if (!smartAwardSettingUpdate) setRecalculation(true);
    }
  };

  const metrics = useMemo(() => {
    if (originalTrucklanes) {
      const rpeTrucklanes =
        activeStep === ACTIVE_STEP.OPPORTUNITIES ? suggestedTrucklanesWithExceptions?.trucklaneExceptionsQuery?.trucklanes || [] : [];
      return generateMetrics(
        user,
        originalTrucklanes,
        initialMetrics,
        Store.invalidTrucklaneChannels.size || recalculation,
        offerShiftsMappedToTrucklanes,
        rpeTrucklanes,
      );
    }
    return initialMetrics;
  }, [
    user,
    recalculation,
    Store.invalidTrucklaneChannels.size,
    originalTrucklanes,
    offerShiftsMappedToTrucklanes,
    suggestedTrucklanesWithExceptions?.trucklaneExceptionsQuery?.trucklanes,
    activeStep,
  ]);

  useEffect(() => {
    if (reservePriceExceptionError) {
      AlertService.alert({
        type: 'warning',
        autoDismiss: true,
        message: <span>{reservePriceExceptionError.message}</span>,
      });
      return;
    }
    if (activeStep === ACTIVE_STEP.OPPORTUNITIES && reservePriceExceptionsEnabled) {
      backgroundRpeRefreshRef.current = rpeFetchedOnceRef.current;
      rpeFetchedOnceRef.current = true;
      getReservePriceExceptionSuggestions();
    }
  }, [activeStep, getReservePriceExceptionSuggestions, reservePriceExceptionsEnabled, reservePriceExceptionError]);

  useEffect(() => {
    if (!reservePriceExceptionLoading) backgroundRpeRefreshRef.current = false;
  }, [reservePriceExceptionLoading]);

  useEffect(() => {
    if (
      activeStep === ACTIVE_STEP.OPPORTUNITIES &&
      reservePriceExceptionsEnabled &&
      suggestedTrucklanesWithExceptions?.trucklaneExceptionsQuery?.errors?.length > 0
    ) {
      AlertService.alert({
        type: 'warning',
        autoDismiss: true,
        message: <span>{suggestedTrucklanesWithExceptions?.trucklaneExceptionsQuery?.errors[0].message}</span>,
      });
    }
  }, [activeStep, reservePriceExceptionsEnabled, suggestedTrucklanesWithExceptions?.trucklaneExceptionsQuery?.errors]);

  useEffect(() => {
    TitleService.setTitles('Truck Lanes');
    Breadcrumbs.set([
      {
        url: '',
        title: 'Offer Comparison',
      },
    ]);
    if (Store.invalidTrucklaneChannels.size) setRecalculation(true);
    return clearCache();
  }, []);

  useEffect(() => {
    // Once the tab is stale, polling is stopped and nothing below applies until the user reloads.
    if (staleTab) return;
    // The server flags a poll as stale when this tab's lanes aren't in the user's current site/cycle
    // (another tab switched impersonation, or the transaction cycle rolled over). Nothing on this page
    // can recover from that, so stop polling and ask for a reload instead of raising the recalc banner.
    if (pollResponse?.stale) {
      setStaleTab(true);
      setSmartAwardSettingUpdate(false);
      setRecalculation(false);
      setPollingData({ poll: false, interval: 5000, initialLoad: false });
      return;
    }
    // Scratch list scoped to this single effect run: built from the poll response, read by the
    // branches below, then discarded. It deliberately does not persist across renders.
    let channels = [];
    // Captured once so a mid-effect change to the ref can't shift the decision between the channel
    // and bid-reallocation checks below. True while we're still absorbing the recalc/reallocation a
    // recent user action (ignore/restore/award) kicked off on the backend.
    const withinActionAbsorbWindow = Date.now() <= skipBidReallocationUntilRef.current;
    if (initPollingData?.trucklaneInitializePollingQuery?.errors.length) {
      AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{initPollingData?.trucklaneInitializePollingQuery?.errors[0]}</span> });
    }
    if (!initPollingData?.trucklaneInitializePollingQuery?.trucklaneChannels.length) {
      setPollingData({ poll: false, interval: 5000, initialLoad: false });
    }
    if (initPollingData?.trucklaneInitializePollingQuery?.trucklaneChannels.length && !channels.length) {
      channels = initPollingData?.trucklaneInitializePollingQuery?.trucklaneChannels;
      setTrucklaneGroups(channels.map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })));
    }
    if (pollResponse?.trucklaneChannels) {
      channels = JSON.parse(JSON.stringify(pollResponse?.trucklaneChannels));
      channels.forEach((channel) => {
        if (Store.trucklaneChannels.has(`${channel.truckType}-${channel.originDCId}`)) {
          const foundChannel = Store.trucklaneChannels.get(`${channel.truckType}-${channel.originDCId}`);
          // A changed updatedAt normally means the channel was recalculated elsewhere, so we flag it
          // stale. But inside the action window the change is the user's own recalc — leave it Valid
          // and let the settle branch below adopt the new updatedAt as the baseline.
          if (!withinActionAbsorbWindow && foundChannel.updatedAt !== channel.updatedAt && channel.status === 'Valid') channel.status = 'Invalid';
        }
      });
    }
    const bidReallocationChanged =
      pollResponse?.bidReallocation &&
      pollingRef.current?.bidReallocation &&
      !pollResponse.bidReallocation.processing &&
      pollingRef.current.bidReallocation.updatedAt !== pollResponse.bidReallocation.updatedAt;
    if (bidReallocationChanged) {
      if (Date.now() <= skipNextBidReallocationUntilRef.current || withinActionAbsorbWindow) {
        // This bump is the reallocation our own recalc/action kicked off — absorb it, don't re-invalidate.
        // The post-refresh absorb is one-shot; the action window stays open for its full duration (see the
        // settle branch), so a single action absorbs both its channel and reallocation bumps.
        skipNextBidReallocationUntilRef.current = 0;
      } else {
        channels.forEach((channel) => Store.invalidateTrucklaneChannel(channel));
        if (!smartAwardSettingUpdate) setRecalculation(true);
      }
    }
    const bidReallocationComplete = !pollResponse?.bidReallocation?.processing;
    if (!smartAwardSettingUpdate && channels.length && channels.every((channel) => channel.status === 'Valid') && bidReallocationComplete) {
      setChannelError(false);
      setPollingData({ poll: true, interval: 5000, initialLoad: false });
      if (!Store.trucklaneChannels.size) Store.setTrucklaneChannels(channels);
      // Inside the action window, keep adopting the freshly polled updatedAt as the new baseline on
      // every settle. We deliberately do NOT close the window early: the backend recalc/reallocation
      // can bump updatedAt several times (and a routine poll may briefly settle before the recalc even
      // lands), so the window must run its full duration. When it expires the baseline is already
      // current, so a genuinely external recalc afterward is still detected.
      if (withinActionAbsorbWindow) Store.setTrucklaneChannels(channels);
    } else if (channels.length && channels.some((channel) => channel.status === 'Errored')) {
      setChannelError(true);
      history.push(`${routePaths.offerComparison}/trucklane`);
      OverlayService.hide();
      setShowDrilldown(false);
      if (!channelError)
        AlertService.alert({
          type: 'warning',
          message: <span>There is an error with the Smart Awarding algorithm. Please reach out to support.</span>,
          autoDismiss: true,
        });
    } else if (loading && !smartAwardSettingUpdate && channels.length) {
      setPollingData({ poll: true, interval: 500, initialLoad: true });
    } else {
      enableRecalculation(channels);
    }
    pollingRef.current = pollResponse;
  }, [initPollingData, pollResponse, staleTab]);

  // After a Recalculate click, check every poll that succeeds, not only ones whose data changed. Once
  // the recalc has finished, every poll returns identical data, which doesn't re-run the poll effect
  // above, so waiting for a change there left the page on the spinner forever. This refreshes even if
  // nothing is marked out of date in the Store; the out-of-sync fetch falls back to a full reload when
  // there is nothing out of sync (NO_OUT_OF_SYNC_CHANNELS_FOUND). recalcPollCount is bumped from
  // usePolling's own effect, so this check runs a render later, after the poll effect has handled the
  // same poll (bid reallocation, invalid lanes); refreshing straight from onPollComplete would reset
  // the Store and baselines before that effect runs.
  useEffect(() => {
    if (smartAwardSettingUpdate && recalcFinished(pollResponse)) {
      setSmartAwardSettingUpdate(false);
      refreshTrucklaneData();
    }
  }, [recalcPollCount]);

  useEffect(() => {
    const errors = trucklaneData?.trucklaneViewQuery?.errors;
    if (!errors?.length || trucklaneDataLoad) return;
    if (errors[0]?.code === NO_OUT_OF_SYNC_CHANNELS_FOUND) {
      // This effect runs again whenever the cached view changes, and the error stays until the reload
      // lands, so only start one.
      if (fullReloadInFlightRef.current) return;
      fullReloadInFlightRef.current = true;
      clearCache();
      setRecalculation(false);
      Store.clearTrucklaneChannels();
      getTrucklaneData({ variables: { getOutOfSyncChannels: false } })
        .catch(() => {
          setFullReloadFailed(true);
          AlertService.alert({ type: 'warning', message: <span>{TRUCKLANES_RELOAD_FAILED}</span>, autoDismiss: true });
        })
        .finally(() => {
          fullReloadInFlightRef.current = false;
        });
      return;
    }
    AlertService.alert({ type: 'warning', message: <span>{errors[0].message}</span>, autoDismiss: true });
  }, [getTrucklaneData, trucklaneData, trucklaneDataLoad]);

  const lastDrilldownLaneRef = useRef({ id: null, lane: null });
  const drilldownLane = useMemo(() => {
    if (!trucklaneId) return null;
    const next =
      reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.OPPORTUNITIES
        ? suggestedTrucklanesWithExceptions?.trucklaneExceptionsQuery?.trucklanes?.find((lane) => lane.id === trucklaneId)
        : originalTrucklanes.find((lane) => lane.id === trucklaneId);
    if (next?.offers?.length > 0) {
      lastDrilldownLaneRef.current = { id: trucklaneId, lane: next };
      return next;
    }
    if (lastDrilldownLaneRef.current.id === trucklaneId) {
      return lastDrilldownLaneRef.current.lane;
    }
    return next ?? null;
  }, [trucklaneId, activeStep, reservePriceExceptionsEnabled, suggestedTrucklanesWithExceptions, originalTrucklanes]);

  const drilldownOffers = useMemo(() => drilldownLane?.offers ?? [], [drilldownLane]);

  useEffect(() => {
    if (Store.invalidTrucklaneChannels.size && !smartAwardSettingUpdate) setRecalculation(true);
  }, [originalTrucklanes, smartAwardSettingUpdate]);

  const recoverErrorState = () => {
    forceRecalculation().then(() => {
      clearCache();
      Store.clearTrucklaneChannels();
      setChannelError(false);
      setRecalculation(false);
      setPollingData({ poll: true, interval: 500, initialLoad: true });
    });
  };

  const openDrilldown = (drilldownTrucklaneId) => {
    setTrucklaneId(drilldownTrucklaneId);
    setShowDrilldown(true);
  };

  const closeDrilldown = () => {
    history.push({ pathname: `${routePaths.offerComparison}/trucklane`, search: history.location.search });
    setShowDrilldown(false);
    // required since flyout has a timeout before closing...
    window.setTimeout(() => {
      setTrucklaneId(null);
    }, 500);
  };

  const needsRecalculation = () => {
    const foundLane = originalTrucklanes.find((lane) => lane.id === trucklaneId);
    if (foundLane) {
      return !!Store.invalidTrucklaneChannels.get(`${foundLane.truckType}-${foundLane.origin}`);
    }
    return false;
  };

  const recalculate = () => {
    // A stale tab has stopped polling, so it would wait forever. The banners are hidden there anyway.
    if (staleTab) return;
    // With no lanes to poll there is nothing to wait for.
    if (initPollingData && !initPollingData.trucklaneInitializePollingQuery?.trucklaneChannels?.length) {
      refreshTrucklaneData();
      return;
    }
    setPollingData({ poll: true, interval: 500, initialLoad: false });
    setSmartAwardSettingUpdate(true);
  };

  // A refresh that found nothing out of sync reloads every lane (see the effect above). Until that lands
  // the cached view carries only this error, which used to count as "no data" and show the "No offers"
  // empty state for the few seconds the reload takes. Derived here rather than set by that effect, so
  // not even the render before the effect runs shows it.
  const viewErrors = trucklaneData?.trucklaneViewQuery?.errors;
  const reloadingAllLanes = viewErrors?.[0]?.code === NO_OUT_OF_SYNC_CHANNELS_FOUND && !fullReloadFailed;
  const pageLoading = loading || reloadingAllLanes;
  const noData = (viewErrors?.length && !reloadingAllLanes && !trucklaneDataLoad) || originalTrucklanes.length === 0;

  return (
    <div>
      {pageLoading && !channelError && <StaticLoading classes={{ container: classes.loader }} />}
      {noData && !pageLoading && !channelError && (
        <div className={`${classes.column} ${classes.container}`}>
          <EmptyStateIcon />
          <h2 className={classes.headerTitle}>No offers have been received yet, check back later</h2>
        </div>
      )}
      {channelError && (
        <div className={`${classes.column} ${classes.container}`}>
          <EmptyStateIcon />
          <h2 className={classes.headerTitle}>
            There is an error with the Smart Awarding algorithm, please recalculate to view truck lanes. If the error persists please reach out to
            support.
          </h2>
          <Button loading={forcedRecalcLoading} loadingText="Recalculating" onClick={recoverErrorState}>
            Recalculate
          </Button>
        </div>
      )}
      {!pageLoading && !noData && !channelError && (
        <div className={classes.container}>
          <div>
            {!saasLiteCustomer && suggestedNegotiationsEnabled && <SuggestedNegotiationsVisibilityBanner history={history} />}
            <h2 className={classes.headerTitle}>Current Cycle Performance</h2>
            <div className={classes.row}>
              {metrics.map((metric, index) => (
                <MetricCard
                  key={index}
                  title={metric.title}
                  current={metric.current}
                  projected={activeStep === ACTIVE_STEP.OPPORTUNITIES ? metric.projectedExceptions : metric.projected}
                  projectedIcon={<SparkleGradientIcon />}
                  projectedTitle={activeStep === ACTIVE_STEP.OPPORTUNITIES ? 'Projected Opportunities' : 'Projected Suggestions'}
                />
              ))}
            </div>
          </div>
          <TrucklaneFilter
            user={user}
            originalTrucklanes={originalTrucklanes}
            rpeTrucklanes={suggestedTrucklanesWithExceptions?.trucklaneExceptionsQuery?.trucklanes}
            rpeLoading={rpeLoading}
            history={history}
            openDrilldown={openDrilldown}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            metrics={metrics}
            recalculation={recalculation || Store.invalidTrucklaneChannels.size > 0}
          />
        </div>
      )}
      {recalculation && !actionInProgress && !smartAwardSettingUpdate && !staleTab && <RecalculationBanner recalculateAction={recalculate} />}
      {staleTab && <StaleTabBanner />}
      <BuyersFlyup className={classes.footer} user={user} history={history} />

      <Route
        path={`${routePaths.offerComparison}/trucklane/:trucklaneId`}
        exact
        render={() => (
          <Flyout
            position="right"
            classes={{
              flyout__container: classes.drilldownContainer,
              flyout__wrapper: classes.drilldown,
              flyout__contents: classes.drilldownContents,
            }}
            open={true}
            onHide={closeDrilldown}
          >
            <TrucklaneDrilldown
              trucklaneId={trucklaneId}
              offers={drilldownOffers}
              recalculation={needsRecalculation()}
              user={user}
              history={history}
              handleDrilldownRecalculation={recalculate}
              loading={pageLoading}
              staleTab={staleTab}
              activeStep={activeStep}
              buyerMinimums={drilldownLane?.buyerMinimums}
            />
          </Flyout>
        )}
      />
      {history.location.pathname.includes(trucklaneId) ? (
        <div id="hideScroll" style={{ display: 'none' }} />
      ) : (
        <div id="showScroll" style={{ display: 'none' }} />
      )}
    </div>
  );
};

Trucklane.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
};

export default Trucklane;
